import { type FormEvent, useState } from 'react';

/**
 * Magic-link request form shared by the /sign-in page and the /book wizard's
 * inline "sign in to confirm" step.
 *
 * Posts `{ email, callbackURL }` to the customer Better Auth scope. The
 * `callbackURL` is where the magic link lands the customer after
 * verification — `/account` for the plain sign-in page, or the wizard URL
 * (with its draft id) when booking, so the prospect returns to exactly the
 * step they left.
 */
export function MagicLinkForm({
  callbackURL,
  onSent,
  submitLabel = 'Send sign-in link',
  inputId = 'sign-in-email',
}: {
  callbackURL: string;
  onSent: (email: string) => void;
  submitLabel?: string | undefined;
  inputId?: string | undefined;
}) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/customer-auth/sign-in/magic-link', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, callbackURL }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        setError(body?.message ?? 'Could not send sign-in link');
        setSubmitting(false);
        return;
      }
      onSent(email);
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <label
        htmlFor={inputId}
        style={{
          display: 'block',
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--muted-foreground)',
          marginBottom: '0.4rem',
        }}
      >
        Email
      </label>
      <input
        id={inputId}
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{
          width: '100%',
          padding: '0.6rem 0.75rem',
          borderRadius: '8px',
          border: '1px solid var(--border, oklch(0.9 0.005 80))',
          fontSize: '0.95rem',
          background: 'var(--background, #fafaf7)',
        }}
      />
      {error ? (
        <p
          role="alert"
          style={{
            color: 'var(--destructive, oklch(0.55 0.16 28))',
            fontSize: '0.8rem',
            marginTop: '0.6rem',
          }}
        >
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={submitting}
        style={{
          width: '100%',
          marginTop: '1.25rem',
          padding: '0.7rem',
          borderRadius: '8px',
          background: 'var(--brand-primary, #004AAD)',
          color: 'white',
          fontWeight: 500,
          fontSize: '0.95rem',
          border: 'none',
          cursor: submitting ? 'default' : 'pointer',
          opacity: submitting ? 0.6 : 1,
        }}
      >
        {submitting ? 'Sending…' : submitLabel}
      </button>
    </form>
  );
}
